import React, {useEffect, useState} from 'react';
import { H1 } from './style';
import axios from 'axios';
import AuthService from '../../../AuthService';
import { PaginationButton, PaginationContainer } from '../../Categories/style';
import { FiPackage, FiClock, FiTruck, FiCheckCircle, FiX, FiCalendar, FiDollarSign, FiUser, FiSearch, FiFilter, FiEye } from 'react-icons/fi';
import { MdStorefront } from 'react-icons/md';
import { Loader } from '../../Login/style';
import Select from 'react-select';


export default function Orders() {

    const [orders, setOrders] = useState([]);
    const [ordersStatistics, setOrdersStatistics] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [pagination, setPagination] = useState({});
    const [loading, setLoading] = useState(false);
    const [limit, setLimit] = useState(50);

    // Filtros
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');

    const fetchOrders = async (page = 1) => {
        setLoading(true);
        try {
            const params = new URLSearchParams({
                page,
                limit
            });

            if (dateFrom) params.append('from', dateFrom);
            if (dateTo) params.append('to', dateTo);

            const response = await axios.get(`https://api.rb.beeapp.es/admin/orders?${params.toString()}`, {
                headers: {
                    'Authorization': 'Bearer ' + AuthService.getToken()
                }
            });

            const data = response.data;
            setOrders(data.results || []);
            setPagination(data.pagination || {});
            setCurrentPage(page);
        } catch (error) {
            console.error('Error fetching orders:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchOrders(currentPage);
    }, [currentPage, limit, dateFrom, dateTo]);

    useEffect(() => {
        // Cargar estadísticas
        axios.get('https://api.rb.beeapp.es/admin/orders/statistics/status', {
            headers: {
                'Authorization': 'Bearer ' + AuthService.getToken()
            }
        }).then(response => {
            setOrdersStatistics(response.data);
        })
        .catch(error => {
            console.error(error);
        });
    }, []);

    // Filtrar órdenes localmente por búsqueda y estado
    const filteredOrders = orders.filter(order => {
        const matchesSearch = !searchTerm ||
            order.UserID?.Name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            order.UserID?.Surname?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            order.UserID?.Email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            order._id.includes(searchTerm);

        const matchesStatus = filterStatus === 'all' || order.Status === filterStatus;

        return matchesSearch && matchesStatus;
    });

    const clearFilters = () => {
        setSearchTerm('');
        setFilterStatus('all');
        setDateFrom('');
        setDateTo('');
        setCurrentPage(1);
    };

    const formatOrderDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('es-ES', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const statusOptions = [
        { value: 'all', label: 'Todos los estados' },
        { value: 'Created', label: 'Pendiente' },
        { value: 'Preparing', label: 'Preparando' },
        { value: 'Shipped', label: 'Enviado' },
        { value: 'Delivered', label: 'Entregado' },
        { value: 'Cancelled', label: 'Cancelado' }
    ];

    const getStatusColor = (status) => {
        switch(status) {
            case 'Created': return { bg: '#fef3c7', color: '#f59e0b', border: '#fde68a' };
            case 'Preparing': return { bg: '#dbeafe', color: '#3b82f6', border: '#bfdbfe' };
            case 'Shipped': return { bg: '#d1fae5', color: '#10b981', border: '#a7f3d0' };
            case 'Delivered': return { bg: '#dcfce7', color: '#16a34a', border: '#bbf7d0' };
            case 'Cancelled': return { bg: '#fee2e2', color: '#ef4444', border: '#fecaca' };
            default: return { bg: '#f3f4f6', color: '#6b7280', border: '#e5e7eb' };
        }
    };

    const getStatusText = (status) => {
        const texts = {
            'Created': 'Pendiente',
            'Preparing': 'Preparando',
            'Shipped': 'Enviado',
            'Delivered': 'Entregado',
            'Cancelled': 'Cancelado'
        };
        return texts[status] || status;
    };

    return (
        <>
        {loading && (
            <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                backgroundColor: 'rgba(0,0,0,0.3)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 100,
            }}>
                <div style={{
                    backgroundColor: '#fff',
                    padding: '32px',
                    borderRadius: '12px',
                    width: 'auto',
                    textAlign: 'center',
                }}>
                    <Loader />
                    <p style={{
                        marginTop: '16px',
                        color: '#6b7280',
                        fontSize: '14px',
                        fontFamily: 'Inter, sans-serif'
                    }}>
                        Cargando pedidos...
                    </p>
                </div>
            </div>
        )}

        <div style={{
            padding: '24px',
            maxWidth: '1400px',
            margin: 'auto'
        }}>
            {/* Header Section */}
            <div style={{
                marginBottom: '32px'
            }}>
                <H1 style={{ marginBottom: '8px' }}>Gestión de Pedidos</H1>
                <p style={{
                    color: '#6b7280',
                    fontSize: '14px',
                    margin: 0,
                    fontFamily: 'Inter, sans-serif'
                }}>
                    Administra y supervisa todos los pedidos de la plataforma
                </p>
            </div>

            {/* Stats Cards */}
            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '16px',
                marginBottom: '24px'
            }}>
                <div style={{
                    backgroundColor: '#fff',
                    border: '2px solid #028322',
                    borderRadius: '12px',
                    padding: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px'
                }}>
                    <div style={{
                        width: '48px',
                        height: '48px',
                        backgroundColor: '#f0fdf4',
                        borderRadius: '10px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}>
                        <FiPackage size={24} color="#028322" />
                    </div>
                    <div>
                        <p style={{ margin: 0, fontSize: '14px', color: '#6b7280', fontFamily: 'Inter, sans-serif' }}>
                            Total Pedidos
                        </p>
                        <p style={{ margin: 0, fontSize: '24px', fontWeight: '600', color: '#111827', fontFamily: 'Inter, sans-serif' }}>
                            {ordersStatistics?.Total || 0}
                        </p>
                    </div>
                </div>

                <div style={{
                    backgroundColor: '#fff',
                    border: '2px solid #f59e0b',
                    borderRadius: '12px',
                    padding: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px'
                }}>
                    <div style={{
                        width: '48px',
                        height: '48px',
                        backgroundColor: '#fef3c7',
                        borderRadius: '10px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}>
                        <FiClock size={24} color="#f59e0b" />
                    </div>
                    <div>
                        <p style={{ margin: 0, fontSize: '14px', color: '#6b7280', fontFamily: 'Inter, sans-serif' }}>
                            Pendientes
                        </p>
                        <p style={{ margin: 0, fontSize: '24px', fontWeight: '600', color: '#111827', fontFamily: 'Inter, sans-serif' }}>
                            {ordersStatistics?.Created ?? 0}
                        </p>
                    </div>
                </div>

                <div style={{
                    backgroundColor: '#fff',
                    border: '2px solid #3b82f6',
                    borderRadius: '12px',
                    padding: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px'
                }}>
                    <div style={{
                        width: '48px',
                        height: '48px',
                        backgroundColor: '#dbeafe',
                        borderRadius: '10px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}>
                        <FiTruck size={24} color="#3b82f6" />
                    </div>
                    <div>
                        <p style={{ margin: 0, fontSize: '14px', color: '#6b7280', fontFamily: 'Inter, sans-serif' }}>
                            Enviados
                        </p>
                        <p style={{ margin: 0, fontSize: '24px', fontWeight: '600', color: '#111827', fontFamily: 'Inter, sans-serif' }}>
                            {(ordersStatistics?.Shipped ?? 0) + (ordersStatistics?.Delivered ?? 0)}
                        </p>
                    </div>
                </div>

                <div style={{
                    backgroundColor: '#fff',
                    border: '2px solid #ef4444',
                    borderRadius: '12px',
                    padding: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px'
                }}>
                    <div style={{
                        width: '48px',
                        height: '48px',
                        backgroundColor: '#fee2e2',
                        borderRadius: '10px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}>
                        <FiX size={24} color="#ef4444" />
                    </div>
                    <div>
                        <p style={{ margin: 0, fontSize: '14px', color: '#6b7280', fontFamily: 'Inter, sans-serif' }}>
                            Cancelados
                        </p>
                        <p style={{ margin: 0, fontSize: '24px', fontWeight: '600', color: '#111827', fontFamily: 'Inter, sans-serif' }}>
                            {ordersStatistics?.Cancelled ?? 0}
                        </p>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div style={{
                backgroundColor: '#fff',
                border: '1px solid #e5e7eb',
                borderRadius: '12px',
                padding: '20px',
                marginBottom: '24px'
            }}>
                <div style={{
                    display: 'flex',
                    gap: '12px',
                    flexWrap: 'wrap',
                    alignItems: 'center'
                }}>
                    {/* Search */}
                    <div style={{ flex: '1', minWidth: '250px', position: 'relative' }}>
                        <FiSearch style={{
                            position: 'absolute',
                            left: '12px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            color: '#9ca3af',
                            pointerEvents: 'none'
                        }} size={16} />
                        <input
                            type="text"
                            placeholder="Buscar por cliente o ID..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={{
                                width: '100%',
                                padding: '10px 16px 10px 36px',
                                border: '1px solid #e5e7eb',
                                borderRadius: '8px',
                                fontSize: '14px',
                                fontFamily: 'Inter, sans-serif',
                                outline: 'none',
                                transition: 'all 0.2s ease'
                            }}
                            onFocus={(e) => e.target.style.borderColor = '#028322'}
                            onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                        />
                    </div>

                    {/* Status Filter */}
                    <div style={{ minWidth: '200px' }}>
                        <Select
                            value={statusOptions.find(opt => opt.value === filterStatus)}
                            onChange={(option) => setFilterStatus(option.value)}
                            options={statusOptions}
                            placeholder="Estado"
                            styles={{
                                control: (base) => ({
                                    ...base,
                                    fontSize: '14px',
                                    fontFamily: 'Inter, sans-serif',
                                    borderColor: '#e5e7eb',
                                    minHeight: '42px'
                                })
                            }}
                        />
                    </div>

                    {/* Date From */}
                    <input
                        type="date"
                        value={dateFrom}
                        onChange={(e) => setDateFrom(e.target.value)}
                        style={{
                            padding: '10px 16px',
                            border: '1px solid #e5e7eb',
                            borderRadius: '8px',
                            fontSize: '14px',
                            fontFamily: 'Inter, sans-serif',
                            outline: 'none'
                        }}
                    />

                    {/* Date To */}
                    <input
                        type="date"
                        value={dateTo}
                        onChange={(e) => setDateTo(e.target.value)}
                        style={{
                            padding: '10px 16px',
                            border: '1px solid #e5e7eb',
                            borderRadius: '8px',
                            fontSize: '14px',
                            fontFamily: 'Inter, sans-serif',
                            outline: 'none'
                        }}
                    />

                    {/* Clear Filters */}
                    {(searchTerm || filterStatus !== 'all' || dateFrom || dateTo) && (
                        <button
                            onClick={clearFilters}
                            style={{
                                padding: '10px 16px',
                                backgroundColor: '#fff',
                                color: '#dc2626',
                                border: '1px solid #dc2626',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                fontSize: '14px',
                                fontWeight: '500',
                                fontFamily: 'Inter, sans-serif',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                                e.target.style.backgroundColor = '#dc2626';
                                e.target.style.color = '#fff';
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.backgroundColor = '#fff';
                                e.target.style.color = '#dc2626';
                            }}
                        >
                            <FiX size={16} />
                            Limpiar
                        </button>
                    )}
                </div>
            </div>

            {/* Orders List */}
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
                marginBottom: '24px'
            }}>
                {filteredOrders.map((order) => {
                    const isBarOffice = order.sourceApp === 'BarOffice';
                    const statusColors = getStatusColor(order.Status);

                    return (
                        <div
                            key={order._id}
                            onClick={() => window.location.href = `/admin/order/${order._id}`}
                            style={{
                                backgroundColor: '#fff',
                                border: '1px solid #e5e7eb',
                                borderRadius: '12px',
                                padding: '20px',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.borderColor = '#028322';
                                e.currentTarget.style.transform = 'translateY(-2px)';
                                e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.borderColor = '#e5e7eb';
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = 'none';
                            }}
                        >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px', flexWrap: 'wrap' }}>
                                {/* Left section */}
                                <div style={{ flex: '1', minWidth: '250px' }}>
                                    {/* Customer info */}
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                                        <div style={{
                                            width: '40px',
                                            height: '40px',
                                            backgroundColor: isBarOffice ? '#dbeafe' : '#f0fdf4',
                                            borderRadius: '8px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}>
                                            <FiUser size={20} color={isBarOffice ? '#1e40af' : '#028322'} />
                                        </div>
                                        <div>
                                            <h3 style={{
                                                margin: 0,
                                                fontSize: '16px',
                                                fontWeight: '600',
                                                color: '#111827',
                                                fontFamily: 'Inter, sans-serif'
                                            }}>
                                                {order.UserID?.Name} {order.UserID?.Surname}
                                            </h3>
                                            <p style={{
                                                margin: 0,
                                                fontSize: '12px',
                                                color: '#6b7280',
                                                fontFamily: 'Inter, sans-serif'
                                            }}>
                                                #{order._id.slice(-8)}
                                            </p>
                                        </div>
                                    </div>

                                    {/* Order details */}
                                    <div style={{
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                                        gap: '12px'
                                    }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <FiCalendar size={14} color="#6b7280" />
                                            <div>
                                                <p style={{ margin: 0, fontSize: '11px', color: '#9ca3af', fontFamily: 'Inter, sans-serif', textTransform: 'uppercase' }}>
                                                    Fecha
                                                </p>
                                                <p style={{ margin: 0, fontSize: '13px', fontWeight: '500', color: '#374151', fontFamily: 'Inter, sans-serif' }}>
                                                    {formatOrderDate(order.CreationDate)}
                                                </p>
                                            </div>
                                        </div>

                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <FiDollarSign size={14} color="#6b7280" />
                                            <div>
                                                <p style={{ margin: 0, fontSize: '11px', color: '#9ca3af', fontFamily: 'Inter, sans-serif', textTransform: 'uppercase' }}>
                                                    Total
                                                </p>
                                                <p style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#028322', fontFamily: 'Inter, sans-serif' }}>
                                                    {order.TotalAmount.toFixed(2).replace('.', ',')}€
                                                </p>
                                            </div>
                                        </div>

                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <FiPackage size={14} color="#6b7280" />
                                            <div>
                                                <p style={{ margin: 0, fontSize: '11px', color: '#9ca3af', fontFamily: 'Inter, sans-serif', textTransform: 'uppercase' }}>
                                                    Productos
                                                </p>
                                                <p style={{ margin: 0, fontSize: '13px', fontWeight: '500', color: '#374151', fontFamily: 'Inter, sans-serif' }}>
                                                    {order.Products?.length || 0} artículo{order.Products?.length !== 1 ? 's' : ''}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Right section - Status & Organization */}
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '12px' }}>
                                    {/* Status badge */}
                                    <span style={{
                                        padding: '6px 12px',
                                        borderRadius: '8px',
                                        fontSize: '12px',
                                        fontWeight: '600',
                                        backgroundColor: statusColors.bg,
                                        color: statusColors.color,
                                        border: `1px solid ${statusColors.border}`,
                                        fontFamily: 'Inter, sans-serif',
                                        whiteSpace: 'nowrap'
                                    }}>
                                        {getStatusText(order.Status)}
                                    </span>

                                    {/* Organization badge */}
                                    {isBarOffice && (
                                        <span style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '4px',
                                            padding: '4px 10px',
                                            borderRadius: '6px',
                                            fontSize: '11px',
                                            fontWeight: '600',
                                            backgroundColor: '#dbeafe',
                                            color: '#1e40af',
                                            border: '1px solid #bfdbfe',
                                            fontFamily: 'Inter, sans-serif'
                                        }}>
                                            <MdStorefront size={12} />
                                            BarOffice
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}

                {filteredOrders.length === 0 && (
                    <div style={{
                        textAlign: 'center',
                        padding: '60px 40px',
                        backgroundColor: '#fff',
                        borderRadius: '12px',
                        border: '1px solid #e5e7eb'
                    }}>
                        <FiPackage size={48} color="#cbd5e1" style={{ marginBottom: '16px' }} />
                        <h3 style={{ color: '#6b7280', margin: '0 0 8px 0', fontWeight: '600', fontFamily: 'Inter, sans-serif' }}>
                            No se encontraron pedidos
                        </h3>
                        <p style={{ color: '#9ca3af', margin: 0, fontFamily: 'Inter, sans-serif' }}>
                            {searchTerm || filterStatus !== 'all' || dateFrom || dateTo
                                ? 'Intenta ajustar los filtros de búsqueda'
                                : 'No hay pedidos disponibles'}
                        </p>
                    </div>
                )}
            </div>

            {/* Pagination */}
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '16px'
            }}>
                <p style={{
                    margin: 0,
                    color: '#6b7280',
                    fontSize: '14px',
                    fontFamily: 'Inter, sans-serif'
                }}>
                    Mostrando {((currentPage - 1) * limit) + 1} - {Math.min(currentPage * limit, pagination.totalOrders || 0)} de {pagination.totalOrders || 0} pedidos
                </p>

                {pagination.totalPages > 1 && (
                    <PaginationContainer>
                        <PaginationButton
                            disabled={currentPage === 1}
                            onClick={() => setCurrentPage(1)}
                            style={{
                                opacity: currentPage === 1 ? 0.5 : 1,
                                cursor: currentPage === 1 ? 'not-allowed' : 'pointer'
                            }}
                        >
                            {'<<'}
                        </PaginationButton>
                        <PaginationButton
                            disabled={currentPage === 1}
                            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                            style={{
                                opacity: currentPage === 1 ? 0.5 : 1,
                                cursor: currentPage === 1 ? 'not-allowed' : 'pointer'
                            }}
                        >
                            {'<'}
                        </PaginationButton>

                        {[...Array(Math.min(5, pagination.totalPages))].map((_, i) => {
                            let pageNum;
                            if (pagination.totalPages <= 5) {
                                pageNum = i + 1;
                            } else if (currentPage <= 3) {
                                pageNum = i + 1;
                            } else if (currentPage >= pagination.totalPages - 2) {
                                pageNum = pagination.totalPages - 4 + i;
                            } else {
                                pageNum = currentPage - 2 + i;
                            }

                            return (
                                <PaginationButton
                                    key={pageNum}
                                    onClick={() => setCurrentPage(pageNum)}
                                    style={{
                                        backgroundColor: currentPage === pageNum ? '#028322' : '#fff',
                                        color: currentPage === pageNum ? '#fff' : '#374151',
                                        borderColor: currentPage === pageNum ? '#028322' : '#e5e7eb',
                                        fontWeight: currentPage === pageNum ? '600' : '400'
                                    }}
                                >
                                    {pageNum}
                                </PaginationButton>
                            );
                        })}

                        <PaginationButton
                            disabled={currentPage === pagination.totalPages}
                            onClick={() => setCurrentPage(prev => Math.min(pagination.totalPages, prev + 1))}
                            style={{
                                opacity: currentPage === pagination.totalPages ? 0.5 : 1,
                                cursor: currentPage === pagination.totalPages ? 'not-allowed' : 'pointer'
                            }}
                        >
                            {'>'}
                        </PaginationButton>
                        <PaginationButton
                            disabled={currentPage === pagination.totalPages}
                            onClick={() => setCurrentPage(pagination.totalPages)}
                            style={{
                                opacity: currentPage === pagination.totalPages ? 0.5 : 1,
                                cursor: currentPage === pagination.totalPages ? 'not-allowed' : 'pointer'
                            }}
                        >
                            {'>>'}
                        </PaginationButton>
                    </PaginationContainer>
                )}

                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                }}>
                    <span style={{
                        color: '#6b7280',
                        fontSize: '14px',
                        fontFamily: 'Inter, sans-serif'
                    }}>
                        Por página:
                    </span>
                    <select
                        value={limit}
                        onChange={(e) => {
                            setLimit(Number(e.target.value));
                            setCurrentPage(1);
                        }}
                        style={{
                            padding: '6px 12px',
                            border: '1px solid #e5e7eb',
                            borderRadius: '6px',
                            fontSize: '14px',
                            fontFamily: 'Inter, sans-serif',
                            outline: 'none',
                            cursor: 'pointer'
                        }}
                    >
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                    </select>
                </div>
            </div>
        </div>
        </>
    );
}
